/* eslint-disable max-lines-per-function */
/* eslint-disable max-statements */
import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@apollo/client";
import { Box, Typography } from "@mui/material";
import logger from "services/logger";
import { useSnackbar } from "components/haven-design/snackbar";
import {
  getManualContract,
  getManualContractVariables,
  getOrganizationsForManualContract,
  getPropertiesForManualContract,
  getSubscriptionsForManualContract,
  getSubscriptionsForManualContractVariables,
  getTeamForManualContract,
  getTeamForManualContractVariables,
  plansAll,
} from "types/gql-types";
import { parseLocalIsoDate } from "utils/date";
import { parseAddress } from "utils/address";
import { GET_PLANS_ALL } from "../plans/queries";
import ManualContractForm, { ManualContractFormValues } from "./form";
import {
  buildOrganizationOptions,
  buildPlanOptions,
  buildPropertyOptions,
  buildSignerOptions,
  buildSubscriptionOptions,
  subscriptionLabel,
  toMutationInput,
} from "./contract-utils";
import {
  GET_MANUAL_CONTRACT,
  GET_ORGANIZATIONS_FOR_MANUAL_CONTRACT,
  GET_PROPERTIES_FOR_MANUAL_CONTRACT,
  GET_SUBSCRIPTIONS_FOR_MANUAL_CONTRACT,
  GET_TEAM_FOR_MANUAL_CONTRACT,
  UPDATE_MANUAL_PROPERTY_CONTRACT,
} from "./queries";

const EditManualContract: React.FC = () => {
  const navigate = useNavigate();
  const { contractId = "" } = useParams();
  const { openSnackbar } = useSnackbar();
  const [sending, setSending] = useState(false);
  const [organizationId, setOrganizationId] = useState("");
  // Scopes the subscription typeahead; must follow the selected property, not the loaded contract.
  const [propertyId, setPropertyId] = useState("");

  const { data: contractData, loading } = useQuery<
  getManualContract,
  getManualContractVariables
  >(GET_MANUAL_CONTRACT, { variables: { id: contractId }, fetchPolicy: "network-only" });

  // errorPolicy "all" keeps the property list usable even when some properties
  // have incomplete (null) address fields, which the schema reports as
  // non-nullable field errors. Without this, Apollo discards the whole result
  // and the property dropdown renders empty ("No options").
  const { data: propertiesData } = useQuery<getPropertiesForManualContract>(
    GET_PROPERTIES_FOR_MANUAL_CONTRACT,
    { fetchPolicy: "network-only", errorPolicy: "all" },
  );
  const { data: plansData } = useQuery<plansAll>(GET_PLANS_ALL, { fetchPolicy: "no-cache" });
  const { data: organizationsData } = useQuery<getOrganizationsForManualContract>(
    GET_ORGANIZATIONS_FOR_MANUAL_CONTRACT,
    { fetchPolicy: "network-only" },
  );
  const { data: teamData } = useQuery<
  getTeamForManualContract,
  getTeamForManualContractVariables
  >(GET_TEAM_FOR_MANUAL_CONTRACT, {
    variables: { id: organizationId },
    skip: !organizationId,
    fetchPolicy: "network-only",
  });

  // List is unmounted here; remount uses cache-and-network. Don't block the snackbar on refetch.
  const [updateManualContract] = useMutation(UPDATE_MANUAL_PROPERTY_CONTRACT);

  const contract = contractData?.propertyContract;

  React.useEffect(() => {
    if (contract?.propertyId) setPropertyId(contract.propertyId);
  }, [contract?.propertyId]);

  const { data: subscriptionsData } = useQuery<
  getSubscriptionsForManualContract,
  getSubscriptionsForManualContractVariables
  >(GET_SUBSCRIPTIONS_FOR_MANUAL_CONTRACT, {
    variables: { propertyId },
    skip: !propertyId,
    fetchPolicy: "network-only",
  });

  const propertyOptions = buildPropertyOptions(propertiesData);
  const selectedProperty = propertyOptions.find((p) => p.value === contract?.propertyId);

  React.useEffect(() => {
    if (selectedProperty?.organizationId) setOrganizationId(selectedProperty.organizationId);
  }, [selectedProperty?.organizationId]);

  const signerOptions = buildSignerOptions(teamData);
  const matchedSigner = (teamData?.organization?.team ?? []).find(
    (user) => contract?.signerEmail && user.email === contract.signerEmail,
  );

  // Must run before the early returns below so hook order stays stable across renders.
  const initialData: ManualContractFormValues = React.useMemo(
    () => ({
      propertyId: contract?.propertyId ?? "",
      organizationId: selectedProperty?.organizationId ?? "",
      planId: contract?.planId ?? "",
      signerUserId: matchedSigner?.id ?? "",
      unitCount: contract?.unitCount ?? "",
      signedAt: parseLocalIsoDate(contract?.signedAt),
      billingFrequency: contract?.billingFrequency ?? "",
      contractStartDate: parseLocalIsoDate(contract?.contractStartDate),
      initialTermEndDate: parseLocalIsoDate(contract?.initialTermEndDate),
      propertyLegalName: contract?.propertyLegalName ?? "",
      status: contract?.status ?? "signed",
      propertyPhone: contract?.propertyPhone ?? "",
      propertyEmail: contract?.propertyEmail ?? "",
      runPostSignSetup: false,
      signedPdfBase64: "",
      signedPdfFilename: "",
      propertyName: contract?.propertyLegalName ?? "",
      subscriptionId: contract?.subscription?.id ?? "",
      ...parseAddress(contract?.propertyAddress),
    }),
    [contract, selectedProperty, matchedSigner],
  );

  if (loading) return <Typography>Loading…</Typography>;
  if (!contract) return <Typography>Contract not found.</Typography>;
  if (!contract.manuallyEntered) {
    return (
      <Typography>This contract was created in-app and cannot be edited here.</Typography>
    );
  }

  // The signer dropdown stores a team-member id, but the contract only knows the
  // signerEmail. Resolving it requires the properties query (to find the org) and
  // that org's team query to finish loading. Mount the form only once the signer
  // can be matched; otherwise Formik initializes with an empty signer and never
  // backfills it (we intentionally avoid enableReinitialize). When the property
  // can't be resolved (e.g. archived), fall through so the form stays usable.
  const signerStillResolving =
    Boolean(contract.signerEmail) &&
    (!propertiesData || (Boolean(selectedProperty) && !teamData));
  if (signerStillResolving) return <Typography>Loading…</Typography>;

  const submitForm = (values: ManualContractFormValues) => {
    setSending(true);
    return updateManualContract({
      variables: { id: contractId, input: toMutationInput(values) },
    })
      .then(() => {
        openSnackbar({ message: "Contract updated", status: "success" });
        navigate("/dash/admin/contracts");
      })
      .catch((err) => {
        logger.sendError(err);
        openSnackbar({
          message:
            err?.graphQLErrors?.[0]?.message ?? err?.message ?? "Failed to update contract",
          status: "error",
        });
      })
      .finally(() => setSending(false));
  };

  const planOptions = buildPlanOptions(plansData);

  return (
    <>
      <Typography variant="h5" gutterBottom>Edit Contract</Typography>
      {contract.subscription && (
        <Box sx={{ mb: 2 }} id="linked-subscription">
          <Typography variant="subtitle2">Linked subscription</Typography>
          <Typography variant="body2">
            {subscriptionLabel(contract.subscription)}
            {contract.subscription.invoicedId ? ` · Invoiced ${contract.subscription.invoicedId}` : ""}
          </Typography>
        </Box>
      )}
      <ManualContractForm
        initialData={initialData}
        sending={sending}
        propertyOptions={propertyOptions}
        planOptions={planOptions}
        signerOptions={signerOptions}
        organizationOptions={buildOrganizationOptions(organizationsData)}
        subscriptionOptions={buildSubscriptionOptions(subscriptionsData)}
        handleSubmit={submitForm}
        onPropertyChange={setOrganizationId}
        onPropertyIdChange={setPropertyId}
        onOrganizationChange={setOrganizationId}
      />
    </>
  );
};

export default EditManualContract;
